import React, { useState, useMemo } from 'react';
import { 
    Box, Card, CardContent, Typography, Grid, TextField, Button, 
    MenuItem, Select, FormControl, InputLabel, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, Paper, Chip, 
    Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, 
    IconButton, Tooltip, Alert 
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KitchenIcon from '@mui/icons-material/Kitchen';
import InventoryIcon from '@mui/icons-material/Inventory';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';

export default function InventoryView() {
    const { 
        inventory, 
        inwardLedger, 
        outwardLedger, 
        orders,
        recipes,
        branches,
        currentUser,
        getActiveBranchId,
        addInwardPurchase, 
        addManualOutward, 
        createInventoryItem,
        updateInventoryItem,
        deleteInventoryItem,
        getReconciliationData,
        syncTenantDataFromDatabase,
        showToast
    } = useApp();

    const { currentLanguage } = useLanguage();
    const isMr = currentLanguage === 'mr';
    const isHi = currentLanguage === 'hi';

    // 🏢 Branch Scoping Logic
    const defaultBranchId = currentUser?.branch_id || (typeof getActiveBranchId === 'function' ? getActiveBranchId() : null) || (branches && branches[0]?.id) || 1;
    const [selectedBranchId, setSelectedBranchId] = useState(() => String(defaultBranchId));

    // Multi-language text dictionary
    const txt = useMemo(() => ({
        title: isMr ? 'किचन इन्व्हेंटरी आणि दैनिक ताळेबंद' : (isHi ? 'किचन इन्वेंटरी और दैनिक स्टॉक' : 'Kitchen Inventory & Stock Audit'),
        subtitle: isMr 
            ? 'थाळ्या व डिशेसच्या खपानुसार चपाती, चिकन, अंडी व कच्च्या साहित्याचा आपोआप अचूक हिशोब' 
            : (isHi ? 'थाली और व्यंजनों की बिक्री के अनुसार सामग्री का स्वचालित स्टॉक हिसाब' : 'Automatic consumption tracking for chapatis, portions, and raw materials based on POS bills'),
        branchLabel: isMr ? 'शाखा निवडा' : (isHi ? 'शाखा चुनें' : 'Branch'),
        refresh: isMr ? 'रिफ्रेश' : (isHi ? 'रिफ्रेश' : 'Refresh'),
        addNewItem: isMr ? '+ नवीन कच्चा माल जोडा' : (isHi ? '+ नया कच्चा माल जोड़ें' : '+ Add Raw Material'),
        totalItems: isMr ? 'शाखा कच्चा माल' : (isHi ? 'शाखा कच्चा माल' : 'Branch Items'),
        todayInward: isMr ? 'आजची आवक साठा' : (isHi ? 'आज की आवक' : "Today's Inward"),
        todaySales: isMr ? 'बिलांनुसार आजचा खप' : (isHi ? 'बिक्री अनुसार खपत' : 'Sales Outward (Sold)'),
        lowStockAlerts: isMr ? 'कमी साठा सूचना' : (isHi ? 'कम स्टॉक अलर्ट' : 'Low Stock Alerts'),
        lowStockBanner: isMr ? 'कमी साठा इशारा:' : (isHi ? 'कम स्टॉक चेतावनी:' : 'Low Stock Alert:'),
        lowStockSub: isMr ? 'खालील साहित्याचा साठा संपत आला आहे:' : (isHi ? 'निम्नलिखित सामग्री का स्टॉक कम है:' : 'The following items are running low on stock:'),
        tab0: isMr ? '📊 दैनिक ताळेबंद व खप हिशोब' : (isHi ? '📊 दैनिक स्टॉक और खपत' : '📊 Daily Stock & Consumption'),
        tab1: isMr ? '📥 माल खरेदी / आवक नोंद' : (isHi ? '📥 माल आवक / खरीद' : '📥 Stock Inward Purchase'),
        tab2: isMr ? '📤 नासाडी / इतर वजावट' : (isHi ? '📤 वेस्टेज / अन्य कटौती' : '📤 Spoilage / Wastage Outward'),
        tab3: isMr ? '📦 कच्चा माल यादी' : (isHi ? '📦 कच्चा माल सूची' : '📦 Raw Material Master'),
        summaryTitle: isMr ? 'आजचा किचन स्टॉक आणि बिलांनुसार खप ताळेबंद' : (isHi ? 'आज का किचन स्टॉक और बिक्री अनुसार खपत' : "Today's Kitchen Stock & Sales Reconciliation"),
        summarySub: isMr ? 'सकाळचा साठा + आवक - (विकलेल्या डिशेस × रेसिपी प्रमाण) = अपेक्षित शिल्लक' : (isHi ? 'प्रारंभिक स्टॉक + आवक - (बिक्री × रेसिपी प्रमाण) = शेष स्टॉक' : 'Opening Stock + Inward - (Sold Dishes × Recipe Portion) = Current Balance'),
        colItem: isMr ? 'कच्चा माल' : (isHi ? 'कच्चा माल' : 'Item Name'),
        colUnit: isMr ? 'युनिट' : (isHi ? 'यूनिट' : 'Unit'),
        colOpening: isMr ? 'सकाळचा साठा' : (isHi ? 'आरंभिक स्टॉक' : 'Opening'),
        colInward: isMr ? 'आजची आवक (+)' : (isHi ? 'आज की आवक (+)' : 'Inward (+)'),
        colSales: isMr ? 'बिलांनुसार खप (-)' : (isHi ? 'बिक्री खपत (-)' : 'Sales (-)'),
        colWastage: isMr ? 'नासाडी (-)' : (isHi ? 'वेस्टेज (-)' : 'Wastage (-)'),
        colClosing: isMr ? 'शिल्लक साठा' : (isHi ? 'शेष स्टॉक' : 'Current Stock'),
        colStatus: isMr ? 'स्थिती' : (isHi ? 'स्थिति' : 'Status'),
        colAction: isMr ? 'त्वरित कृती' : (isHi ? 'कार्रवाई' : 'Quick Action'),
        btnInward: isMr ? '+ आवक' : (isHi ? '+ आवक' : '+ Inward'),
        btnOutward: isMr ? '- नासाडी' : (isHi ? '- वेस्टेज' : '- Wastage'),
        statusLow: isMr ? 'कमी साठा' : (isHi ? 'कम स्टॉक' : 'Low Stock'),
        statusGood: isMr ? 'मुबलक' : (isHi ? 'पर्याप्त' : 'Optimal'),
        emptyRecon: isMr ? "या शाखेसाठी कोणताही कच्चा माल जोडलेला नाही. वर दिलेल्या '+ नवीन कच्चा माल जोडा' बटणावर क्लिक करा!" : (isHi ? "इस शाखा के लिए कोई कच्चा माल नहीं मिला। '+ नया कच्चा माल जोड़ें' पर क्लिक करें!" : "No raw materials found for this branch. Click '+ Add Raw Material' above!"),
        inwardCardTitle: isMr ? 'माल आवक / खरेदी नोंदवा' : (isHi ? 'माल आवक / खरीद दर्ज करें' : 'Record Stock Inward / Purchase'),
        inwardCardSub: isMr ? 'सकाळी किंवा दिवसात नवीन आलेला कच्चा माल (उदा. चपात्या, चिकन, पीठ) येथे नोंदवा.' : (isHi ? 'नई खरीदी या आने वाली सामग्री यहाँ दर्ज करें।' : 'Log fresh raw materials arriving at the kitchen or counter.'),
        selectMaterial: isMr ? 'कच्चा माल निवडा' : (isHi ? 'कच्चा माल चुनें' : 'Select Raw Material'),
        qtyLabel: isMr ? 'आलेली संख्या / प्रमाण' : (isHi ? 'आवक मात्रा / संख्या' : 'Quantity Received'),
        rateLabel: isMr ? 'दर प्रति युनिट (₹ - ऐच्छिक)' : (isHi ? 'दर प्रति यूनिट (₹ - वैकल्पिक)' : 'Rate per Unit (₹ - Optional)'),
        vendorLabel: isMr ? 'पुरवठादार / सप्लायर (ऐच्छिक)' : (isHi ? 'सप्लायर / वेंडर (वैकल्पिक)' : 'Supplier / Vendor (Optional)'),
        btnSubmitInward: isMr ? '+ साठा जमा करा' : (isHi ? '+ स्टॉक जमा करें' : '+ Add to Stock'),
        recentInwardTitle: isMr ? 'नुकतीच झालेली माल आवक नोंदवही' : (isHi ? 'हालिया माल आवक रिकॉर्ड' : 'Recent Inward Purchase Log'),
        dateCol: isMr ? 'तारीख' : (isHi ? 'दिनांक' : 'Date'),
        qtyCol: isMr ? 'जमा संख्या' : (isHi ? 'मात्रा' : 'Quantity'),
        rateCol: isMr ? 'दर' : (isHi ? 'दर' : 'Rate'),
        vendorCol: isMr ? 'पुरवठादार' : (isHi ? 'वेंडर' : 'Vendor'),
        outwardCardTitle: isMr ? 'नासाडी / इतर वजावट नोंदवा' : (isHi ? 'वेस्टेज / अन्य कटौती दर्ज करें' : 'Log Wastage / Outward'),
        outwardCardSub: isMr ? 'खराब झालेला माल, कर्मचारी जेवण किंवा नासाडीची नोंद येथून करा.' : (isHi ? 'खराब सामग्री या स्टाफ भोजन की कटौती यहाँ दर्ज करें।' : 'Deduct spoiled food, staff meals, or complimentary samples.'),
        deductQtyLabel: isMr ? 'कमी झालेली संख्या / प्रमाण' : (isHi ? 'कटौती मात्रा' : 'Deducted Quantity'),
        reasonLabel: isMr ? 'कारण' : (isHi ? 'कारण' : 'Reason'),
        reasonWastage: isMr ? 'नासाडी / खराब माल (Wastage)' : (isHi ? 'खराब सामग्री (Wastage)' : 'Spoilage / Wastage'),
        reasonStaff: isMr ? 'कर्मचारी जेवण (Staff Meal)' : (isHi ? 'स्टाफ भोजन (Staff Meal)' : 'Staff Meal'),
        reasonSample: isMr ? 'नमुना / कॉम्प्लिमेंटरी' : (isHi ? 'सैंपल / कॉम्प्लिमेंटरी' : 'Promo / Free Sample'),
        reasonBreakage: isMr ? 'गळती / तुटफूट' : (isHi ? 'टूटफूट / रिसाव' : 'Spillage / Breakage'),
        btnSubmitOutward: isMr ? '- साठा वजा करा' : (isHi ? '- स्टॉक घटाएं' : '- Deduct Stock'),
        recentOutwardTitle: isMr ? 'नासाडी व कपात नोंदवही' : (isHi ? 'वेस्टेज व कटौती रिकॉर्ड' : 'Wastage & Outward Log History'),
        masterTitle: isMr ? 'कच्चा माल मास्टर यादी' : (isHi ? 'कच्चा माल मास्टर सूची' : 'Raw Materials Master List'),
        masterSub: isMr ? 'फक्त निवडलेल्या शाखेतील कच्च्या मालाचे नाव, मोजणी युनिट आणि कमी साठा अलर्ट व्यवस्थापित करा.' : (isHi ? 'केवल चयनित शाखा की कच्ची सामग्री का प्रबंधन करें।' : 'Manage raw materials strictly for the selected branch.'),
        currentStockCol: isMr ? 'चालू साठा' : (isHi ? 'वर्तमान स्टॉक' : 'Current Stock'),
        alertLevelCol: isMr ? 'कमी साठा सूचना स्तर' : (isHi ? 'अलर्ट स्तर' : 'Alert Level'),
        actionsCol: isMr ? 'कृती' : (isHi ? 'कार्रवाई' : 'Actions'),
        createModalTitle: isMr ? '🥬 नवीन कच्चा माल तयार करा' : (isHi ? '🥬 नया कच्चा माल जोड़ें' : '🥬 Create New Raw Material'),
        itemNameLabel: isMr ? 'कच्च्या मालाचे नाव' : (isHi ? 'कच्चे माल का नाम' : 'Raw Material Name'),
        unitLabel: isMr ? 'मोजणीचे युनिट' : (isHi ? 'इकाई (Unit)' : 'Measurement Unit'),
        openStockLabel: isMr ? 'सुरुवातीचा साठा (Opening Stock)' : (isHi ? 'प्रारंभिक स्टॉक' : 'Initial / Opening Stock'),
        openStockHelper: isMr ? 'सकाळी काउंटरवर किंवा किचनमध्ये किती उपलब्ध आहे' : (isHi ? 'किचन में वर्तमान उपलब्ध मात्रा' : 'Available quantity at the start of shift'),
        cancel: isMr ? 'रद्द करा' : (isHi ? 'रद्द करें' : 'Cancel'),
        save: isMr ? 'सेव्ह करा' : (isHi ? 'सेव करें' : 'Save Item'),
        editModalTitle: isMr ? '✏️ कच्चा माल माहिती बदला' : (isHi ? '✏️ कच्चा माल संपादित करें' : '✏️ Edit Raw Material'),
        update: isMr ? 'अपडेट करा' : (isHi ? 'अपडेट करें' : 'Update')
    }), [isMr, isHi]);

    const [tabVal, setTabVal] = useState(0);

    // Modal States
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newItemName, setNewItemName] = useState('');
    const [newItemUnit, setNewItemUnit] = useState('नग');
    const [newItemQty, setNewItemQty] = useState('50');
    const [newItemThreshold, setNewItemThreshold] = useState('10');

    // Quick Inward / Outward Modal States
    const [quickModal, setQuickModal] = useState({ open: false, type: 'inward', item: null });
    const [quickQty, setQuickQty] = useState('');
    const [quickRate, setQuickRate] = useState('');
    const [quickVendor, setQuickVendor] = useState('');
    const [quickReason, setQuickReason] = useState('Spoilage / Wastage');

    // Edit Item Modal State
    const [editModal, setEditModal] = useState({ open: false, item: null });
    const [editName, setEditName] = useState('');
    const [editUnit, setEditUnit] = useState('नग');
    const [editQty, setEditQty] = useState('');
    const [editThreshold, setEditThreshold] = useState('');

    // Tab 1 Form (Inward Purchase)
    const [inwardIng, setInwardIng] = useState('');
    const [inwardQty, setInwardQty] = useState('');
    const [inwardRate, setInwardRate] = useState('');
    const [inwardVendor, setInwardVendor] = useState('');

    // Tab 2 Form (Manual Outward)
    const [outwardIng, setOutwardIng] = useState('');
    const [outwardQty, setOutwardQty] = useState('');
    const [outwardReason, setOutwardReason] = useState('Spoilage / Wastage');

    const getIngredientName = (item) => {
        if (!item) return '';
        if (isMr) return item.nameMr || item.name_mr || item.name;
        if (isHi) return item.nameHi || item.name_hi || item.name;
        return item.name;
    };

    // 🛡️ 1. Filter raw materials strictly by the selected branch (Eliminates all cross-branch duplicates!)
    const branchInventory = useMemo(() => {
        return (inventory || []).filter(item => {
            if (!selectedBranchId) return true;
            return String(item.branch_id) === String(selectedBranchId);
        });
    }, [inventory, selectedBranchId]);

    // ⚡ 2. Memoized Reconciliation Data strictly for the selected branch (0ms lag!)
    const reconData = useMemo(() => {
        return getReconciliationData(selectedBranchId) || [];
    }, [getReconciliationData, selectedBranchId, inventory, inwardLedger, outwardLedger, orders, recipes]);

    // ⚡ 3. Branch-scoped Summary Metrics
    const metrics = useMemo(() => {
        const total = branchInventory.length;
        const low = branchInventory.filter(item => (Number(item.qty) || 0) <= (Number(item.threshold) || 0));
        const sales = reconData.reduce((acc, r) => acc + (Number(r.salesOutward) || 0), 0);
        const inward = reconData.reduce((acc, r) => acc + (Number(r.inward) || 0), 0);
        return { total, low, sales, inward };
    }, [branchInventory, reconData]);

    // Create New Item Handler (Tagged strictly with selectedBranchId)
    const handleCreateItem = async (e) => {
        if (e) e.preventDefault();
        if (!newItemName.trim()) {
            showToast(isMr ? 'कृपया कच्च्या मालाचे नाव टाका.' : 'Please enter item name.');
            return;
        }
        await createInventoryItem({
            branch_id: parseInt(selectedBranchId) || 1,
            name: newItemName.trim(),
            unit: newItemUnit,
            qty: parseFloat(newItemQty) || 0,
            threshold: parseFloat(newItemThreshold) || 5
        });
        setCreateModalOpen(false);
        setNewItemName('');
        setNewItemQty('50');
    };

    // Quick Action Submit
    const handleQuickActionSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!quickModal.item || !quickQty || parseFloat(quickQty) <= 0) return;

        if (quickModal.type === 'inward') {
            await addInwardPurchase(quickModal.item.id, quickQty, quickRate, quickVendor);
        } else {
            await addManualOutward(quickModal.item.id, quickQty, quickReason);
        }

        setQuickModal({ open: false, type: 'inward', item: null });
        setQuickQty('');
        setQuickRate('');
        setQuickVendor('');
    };

    // Edit Item Submit
    const handleEditItemSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!editModal.item || !editName.trim()) return;

        await updateInventoryItem(editModal.item.id, {
            name: editName.trim(),
            unit: editUnit,
            qty: parseFloat(editQty) || 0,
            threshold: parseFloat(editThreshold) || 0
        });

        setEditModal({ open: false, item: null });
    };

    // Delete Item
    const handleDeleteItem = async (item) => {
        const confirmMsg = isMr 
            ? `तुम्हाला '${getIngredientName(item)}' हा कच्चा माल खरोखर हटवायचा आहे का?` 
            : `Are you sure you want to delete '${getIngredientName(item)}'?`;
        if (window.confirm(confirmMsg)) {
            await deleteInventoryItem(item.id);
        }
    };

    // Tab Inward Form Submit
    const handleInwardSubmit = async (e) => {
        e.preventDefault();
        if (!inwardIng || !inwardQty) return;
        await addInwardPurchase(inwardIng, inwardQty, inwardRate, inwardVendor);
        setInwardIng('');
        setInwardQty('');
        setInwardRate('');
        setInwardVendor('');
    };

    // Tab Outward Form Submit
    const handleOutwardSubmit = async (e) => {
        e.preventDefault();
        if (!outwardIng || !outwardQty || !outwardReason) return;
        await addManualOutward(outwardIng, outwardQty, outwardReason);
        setOutwardIng('');
        setOutwardQty('');
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc', minHeight: 'calc(100vh - 70px)' }}>
            
            {/* Header & Main Actions */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <KitchenIcon sx={{ color: '#008751', fontSize: 32 }} />
                        {txt.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mt: 0.5 }}>
                        {txt.subtitle}
                    </Typography>
                </Box>
                
                {/* Branch Selector + Action Buttons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {Array.isArray(branches) && branches.length > 1 && (
                        <FormControl size="small" sx={{ minWidth: 200, bgcolor: '#ffffff', borderRadius: 2 }}>
                            <InputLabel sx={{ fontWeight: 700 }}>{txt.branchLabel}</InputLabel>
                            <Select
                                value={selectedBranchId}
                                label={txt.branchLabel}
                                onChange={(e) => setSelectedBranchId(e.target.value)}
                                sx={{ fontWeight: 800, '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 } }}
                            >
                                {branches.map(b => (
                                    <MenuItem key={b.id} value={String(b.id)}>
                                        🏢 {b.name || b.branch_name || `Branch #${b.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={syncTenantDataFromDatabase}
                        startIcon={<RefreshIcon />}
                        sx={{ bgcolor: '#ffffff', fontWeight: 700, borderColor: '#cbd5e1', textTransform: 'none' }}
                    >
                        {txt.refresh}
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => setCreateModalOpen(true)}
                        startIcon={<AddIcon />}
                        sx={{ bgcolor: '#008751', fontWeight: 800, textTransform: 'none', px: 2, '&:hover': { bgcolor: '#006f42' } }}
                    >
                        {txt.addNewItem}
                    </Button>
                </Box>
            </Box>

            {/* Quick Metrics Bar */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                                {txt.totalItems}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a', mt: 0.5 }}>
                                {metrics.total}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #bbf7d0', bgcolor: '#f0fdf4', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="caption" sx={{ color: '#166534', fontWeight: 700, textTransform: 'uppercase' }}>
                                {txt.todayInward}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#15803d', mt: 0.5 }}>
                                +{metrics.inward.toFixed(0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #fed7aa', bgcolor: '#fff7ed', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="caption" sx={{ color: '#9a3412', fontWeight: 700, textTransform: 'uppercase' }}>
                                {txt.todaySales}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#c2410c', mt: 0.5 }}>
                                -{metrics.sales.toFixed(0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: 3, border: metrics.low.length > 0 ? '1px solid #fecdd3' : '1px solid #e2e8f0', bgcolor: metrics.low.length > 0 ? '#fff1f2' : '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="caption" sx={{ color: metrics.low.length > 0 ? '#9f1239' : '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                                {txt.lowStockAlerts}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: metrics.low.length > 0 ? '#e11d48' : '#0f172a', mt: 0.5 }}>
                                {metrics.low.length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Low stock alert banner */}
            {metrics.low.length > 0 && (
                <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, borderRadius: 2, fontWeight: 600 }}>
                    <strong>{txt.lowStockBanner}</strong> {txt.lowStockSub} {metrics.low.map(i => `${getIngredientName(i)} (${i.qty} ${i.unit})`).join(', ')}.
                </Alert>
            )}

            {/* Navigation Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0', mb: 3 }}>
                <Tabs 
                    value={tabVal} 
                    onChange={(e, val) => setTabVal(val)} 
                    textColor="primary" 
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTab-root': { fontWeight: 800, textTransform: 'none', fontSize: '0.95rem' }
                    }}
                >
                    <Tab label={txt.tab0} />
                    <Tab label={txt.tab1} />
                    <Tab label={txt.tab2} />
                    <Tab label={txt.tab3} />
                </Tabs>
            </Box>

            {/* TAB 0: Daily Stock & Consumption Summary (Scoped strictly to selectedBranchId) */}
            {tabVal === 0 && (
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountBalanceWalletIcon sx={{ color: '#008751' }} />
                                    {txt.summaryTitle}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                    {txt.summarySub}
                                </Typography>
                            </Box>
                        </Box>

                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800 }}>{txt.colItem}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>{txt.colUnit}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }} align="right">{txt.colOpening}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }} align="right">{txt.colInward}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }} align="right">{txt.colSales}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }} align="right">{txt.colWastage}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }} align="right">{txt.colClosing}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }} align="center">{txt.colStatus}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }} align="center">{txt.colAction}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reconData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#94a3b8', fontWeight: 600 }}>
                                                {txt.emptyRecon}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        reconData.map((rec) => {
                                            const rawItem = branchInventory.find(i => i.id === rec.id || parseInt(i.id) === parseInt(rec.id));
                                            const threshold = Number(rawItem?.threshold) || 5;
                                            const isLow = rec.closing <= threshold;

                                            return (
                                                <TableRow key={rec.id} sx={{ bgcolor: isLow ? '#fef2f2' : 'inherit', '&:hover': { bgcolor: '#f8fafc' } }}>
                                                    <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>
                                                        {getIngredientName(rec)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={rec.unit} 
                                                            size="small" 
                                                            sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f1f5f9', color: '#475569' }} 
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569' }}>
                                                        {rec.opening}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#16a34a' }}>
                                                        +{rec.inward}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 800, color: '#ea580c' }}>
                                                        -{rec.salesOutward}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#dc2626' }}>
                                                        -{rec.manualOutward}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: isLow ? '#dc2626' : '#0f172a', fontSize: '1rem' }}>
                                                        {rec.closing} {rec.unit}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={isLow ? txt.statusLow : txt.statusGood}
                                                            size="small"
                                                            color={isLow ? 'error' : 'success'}
                                                            sx={{ fontWeight: 800, fontSize: '0.7rem', height: 22 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                            <Tooltip title={isMr ? "साठा आवक नोंदवा (Inward)" : "Add Stock Inward"}>
                                                                <Button 
                                                                    size="small" 
                                                                    variant="contained" 
                                                                    color="success"
                                                                    onClick={() => {
                                                                        setQuickModal({ open: true, type: 'inward', item: rawItem || rec });
                                                                        setQuickQty('');
                                                                    }}
                                                                    sx={{ minWidth: 28, px: 1, py: 0.3, fontSize: '0.75rem', fontWeight: 800, textTransform: 'none' }}
                                                                >
                                                                    {txt.btnInward}
                                                                </Button>
                                                            </Tooltip>
                                                            <Tooltip title={isMr ? "नासाडी नोंदवा (Wastage)" : "Deduct Wastage"}>
                                                                <Button 
                                                                    size="small" 
                                                                    variant="outlined" 
                                                                    color="error"
                                                                    onClick={() => {
                                                                        setQuickModal({ open: true, type: 'outward', item: rawItem || rec });
                                                                        setQuickQty('');
                                                                    }}
                                                                    sx={{ minWidth: 28, px: 1, py: 0.3, fontSize: '0.75rem', fontWeight: 800, textTransform: 'none' }}
                                                                >
                                                                    {txt.btnOutward}
                                                                </Button>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* TAB 1: Inward Purchase Entry & Log */}
            {tabVal === 1 && (
                <Grid container spacing={3}>
                    {/* Inward Form */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocalShippingIcon sx={{ color: '#008751' }} />
                                    {txt.inwardCardTitle}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
                                    {txt.inwardCardSub}
                                </Typography>

                                <Box component="form" onSubmit={handleInwardSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{txt.selectMaterial}</InputLabel>
                                        <Select
                                            value={inwardIng}
                                            label={txt.selectMaterial}
                                            onChange={(e) => setInwardIng(e.target.value)}
                                            required
                                        >
                                            {branchInventory.map(i => (
                                                <MenuItem key={i.id} value={i.id}>
                                                    {getIngredientName(i)} ({i.unit})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        label={txt.qtyLabel}
                                        type="number"
                                        size="small"
                                        placeholder={isMr ? "उदा. 50 किंवा 100" : "e.g. 50 or 100"}
                                        value={inwardQty}
                                        onChange={(e) => setInwardQty(e.target.value)}
                                        required
                                        fullWidth
                                    />

                                    <TextField
                                        label={txt.rateLabel}
                                        type="number"
                                        size="small"
                                        placeholder={isMr ? "उदा. 120" : "e.g. 120"}
                                        value={inwardRate}
                                        onChange={(e) => setInwardRate(e.target.value)}
                                        fullWidth
                                    />

                                    <TextField
                                        label={txt.vendorLabel}
                                        size="small"
                                        placeholder={isMr ? "उदा. स्थानिक बेकरी / चिकन सेंटर" : "e.g. Local Bakery / Supplier"}
                                        value={inwardVendor}
                                        onChange={(e) => setInwardVendor(e.target.value)}
                                        fullWidth
                                    />

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        sx={{ 
                                            mt: 1, 
                                            py: 1, 
                                            fontWeight: 800, 
                                            textTransform: 'none', 
                                            borderRadius: 2,
                                            bgcolor: '#008751',
                                            '&:hover': { bgcolor: '#006f42' }
                                        }}
                                    >
                                        {txt.btnSubmitInward}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Inward Ledger */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>
                                    {txt.recentInwardTitle}
                                </Typography>

                                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 800 }}>{txt.dateCol}</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>{txt.colItem}</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }} align="right">{txt.qtyCol}</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }} align="right">{txt.rateCol}</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>{txt.vendorCol}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(inwardLedger || []).filter(rec => !rec.branch_id || String(rec.branch_id) === String(selectedBranchId)).length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#94a3b8' }}>
                                                        {isMr ? 'या शाखेसाठी कोणतीही आवक नोंद नाही.' : 'No inward purchase history for this branch.'}
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                (inwardLedger || []).filter(rec => !rec.branch_id || String(rec.branch_id) === String(selectedBranchId)).map((rec) => (
                                                    <TableRow key={rec.id}>
                                                        <TableCell sx={{ fontWeight: 600 }}>
                                                            {rec.date ? new Date(rec.date).toLocaleDateString() : (isMr ? 'आज' : 'Today')}
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                                                            {rec.ingName || rec.ingredient_name || `Item #${rec.ingId}`}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ color: '#16a34a', fontWeight: 800 }}>
                                                            +{rec.qty || rec.quantity}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {rec.rate ? `₹${rec.rate}` : '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#64748b' }}>
                                                            {rec.vendor || (isMr ? 'थेट खरेदी' : 'Direct Purchase')}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* TAB 2: Outward / Wastage Entry & Log */}
            {tabVal === 2 && (
                <Grid container spacing={3}>
                    {/* Outward Form */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DeleteSweepIcon sx={{ color: '#dc2626' }} />
                                    {txt.outwardCardTitle}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
                                    {txt.outwardCardSub}
                                </Typography>

                                <Box component="form" onSubmit={handleOutwardSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{txt.selectMaterial}</InputLabel>
                                        <Select
                                            value={outwardIng}
                                            label={txt.selectMaterial}
                                            onChange={(e) => setOutwardIng(e.target.value)}
                                            required
                                        >
                                            {branchInventory.map(i => (
                                                <MenuItem key={i.id} value={i.id}>
                                                    {getIngredientName(i)} ({i.unit})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        label={txt.deductQtyLabel}
                                        type="number"
                                        size="small"
                                        placeholder={isMr ? "उदा. 3 किंवा 5" : "e.g. 3 or 5"}
                                        value={outwardQty}
                                        onChange={(e) => setOutwardQty(e.target.value)}
                                        required
                                        fullWidth
                                    />

                                    <FormControl fullWidth size="small">
                                        <InputLabel>{txt.reasonLabel}</InputLabel>
                                        <Select
                                            value={outwardReason}
                                            label={txt.reasonLabel}
                                            onChange={(e) => setOutwardReason(e.target.value)}
                                            required
                                        >
                                            <MenuItem value="Spoilage / Wastage">{txt.reasonWastage}</MenuItem>
                                            <MenuItem value="Staff Food">{txt.reasonStaff}</MenuItem>
                                            <MenuItem value="Promo / Free Sample">{txt.reasonSample}</MenuItem>
                                            <MenuItem value="Spillage / Breakage">{txt.reasonBreakage}</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="error"
                                        sx={{ 
                                            mt: 1, 
                                            py: 1, 
                                            fontWeight: 800, 
                                            textTransform: 'none', 
                                            borderRadius: 2
                                        }}
                                    >
                                        {txt.btnSubmitOutward}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Outward Ledger */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>
                                    {txt.recentOutwardTitle}
                                </Typography>

                                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 800 }}>{txt.dateCol}</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>{txt.colItem}</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }} align="right">{txt.qtyCol}</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>{txt.reasonLabel}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(outwardLedger || []).filter(rec => !rec.branch_id || String(rec.branch_id) === String(selectedBranchId)).length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#94a3b8' }}>
                                                        {isMr ? 'या शाखेसाठी कोणतीही नासाडी नोंद नाही.' : 'No outward wastage logs for this branch.'}
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                (outwardLedger || []).filter(rec => !rec.branch_id || String(rec.branch_id) === String(selectedBranchId)).map((rec) => (
                                                    <TableRow key={rec.id}>
                                                        <TableCell sx={{ fontWeight: 600 }}>
                                                            {rec.date ? new Date(rec.date).toLocaleDateString() : (isMr ? 'आज' : 'Today')}
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                                                            {rec.ingName || rec.ingredient_name || `Item #${rec.ingId}`}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 800 }}>
                                                            -{rec.qty}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip 
                                                                label={rec.reason} 
                                                                size="small" 
                                                                variant="outlined" 
                                                                color={rec.reason?.includes('Wastage') || rec.reason?.includes('नासाडी') ? 'error' : 'default'}
                                                                sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22 }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* TAB 3: Raw Materials Master Management (Scoped strictly to selectedBranchId) */}
            {tabVal === 3 && (
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InventoryIcon sx={{ color: '#008751' }} />
                                    {txt.masterTitle}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    {txt.masterSub}
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => setCreateModalOpen(true)}
                                startIcon={<AddIcon />}
                                sx={{ bgcolor: '#008751', fontWeight: 800, textTransform: 'none', '&:hover': { bgcolor: '#006f42' } }}
                            >
                                {txt.addNewItem}
                            </Button>
                        </Box>

                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800 }}>{txt.colItem}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>{txt.colUnit}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }} align="right">{txt.currentStockCol}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }} align="right">{txt.alertLevelCol}</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }} align="center">{txt.actionsCol}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {branchInventory.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                                                {txt.emptyRecon}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        branchInventory.map(item => (
                                            <TableRow key={item.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>
                                                    {getIngredientName(item)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={item.unit} size="small" sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }} />
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 800 }}>
                                                    {item.qty} {item.unit}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#64748b', fontWeight: 600 }}>
                                                    {item.threshold} {item.unit}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => {
                                                            setEditModal({ open: true, item });
                                                            setEditName(item.name || '');
                                                            setEditUnit(item.unit || 'नग');
                                                            setEditQty(String(item.qty || '0'));
                                                            setEditThreshold(String(item.threshold || '5'));
                                                        }}
                                                        title={isMr ? "माहिती बदला" : "Edit"}
                                                    >
                                                        <EditIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteItem(item)}
                                                        title={isMr ? "हटवा" : "Delete"}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* DIALOG 1: Create New Raw Material */}
            <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>
                    {txt.createModalTitle}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        autoFocus
                        label={txt.itemNameLabel}
                        placeholder={isMr ? "उदा. चपाती किंवा चिकन वाटी" : "e.g. Chapati or Chicken Portion"}
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        fullWidth
                        size="small"
                        required
                    />
                    <FormControl fullWidth size="small">
                        <InputLabel>{txt.unitLabel}</InputLabel>
                        <Select
                            value={newItemUnit}
                            label={txt.unitLabel}
                            onChange={(e) => setNewItemUnit(e.target.value)}
                        >
                            <MenuItem value="नग">{isMr ? "नग (Pcs / Number)" : "Pcs / Number (नग)"}</MenuItem>
                            <MenuItem value="वाटी">{isMr ? "वाटी / पोर्शन (Bowl / Portion)" : "Bowl / Portion (वाटी)"}</MenuItem>
                            <MenuItem value="प्लेट">{isMr ? "प्लेट (Plate)" : "Plate (प्लेट)"}</MenuItem>
                            <MenuItem value="kg">{isMr ? "किलो (kg)" : "Kilogram (kg)"}</MenuItem>
                            <MenuItem value="ग्रॅम">{isMr ? "ग्रॅम (Grams)" : "Grams (gm)"}</MenuItem>
                            <MenuItem value="लिटर">{isMr ? "लिटर (Litre)" : "Litre (ltr)"}</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label={txt.openStockLabel}
                        type="number"
                        value={newItemQty}
                        onChange={(e) => setNewItemQty(e.target.value)}
                        fullWidth
                        size="small"
                        helperText={txt.openStockHelper}
                    />
                    <TextField
                        label={txt.alertLevelCol}
                        type="number"
                        value={newItemThreshold}
                        onChange={(e) => setNewItemThreshold(e.target.value)}
                        fullWidth
                        size="small"
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setCreateModalOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>
                        {txt.cancel}
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleCreateItem} 
                        sx={{ bgcolor: '#008751', fontWeight: 800, '&:hover': { bgcolor: '#006f42' } }}
                    >
                        {txt.save}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG 2: Quick Inward / Outward Modal */}
            <Dialog open={quickModal.open} onClose={() => setQuickModal({ open: false, type: 'inward', item: null })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, color: quickModal.type === 'inward' ? '#166534' : '#991b1b' }}>
                    {quickModal.type === 'inward' 
                        ? (isMr ? '📥 त्वरित साठा आवक (Inward)' : '📥 Quick Stock Inward') 
                        : (isMr ? '📤 त्वरित साठा कपात (Outward)' : '📤 Quick Stock Outward')}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {txt.colItem}: <strong>{getIngredientName(quickModal.item)}</strong> ({txt.colUnit}: {quickModal.item?.unit})
                    </Typography>
                    
                    <TextField
                        autoFocus
                        label={quickModal.type === 'inward' ? `${txt.qtyLabel} (${quickModal.item?.unit || ''})` : `${txt.deductQtyLabel} (${quickModal.item?.unit || ''})`}
                        type="number"
                        placeholder={isMr ? "उदा. 20" : "e.g. 20"}
                        value={quickQty}
                        onChange={(e) => setQuickQty(e.target.value)}
                        fullWidth
                        size="small"
                        required
                    />

                    {quickModal.type === 'inward' ? (
                        <>
                            <TextField
                                label={txt.rateLabel}
                                type="number"
                                placeholder={isMr ? "उदा. 120" : "e.g. 120"}
                                value={quickRate}
                                onChange={(e) => setQuickRate(e.target.value)}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label={txt.vendorLabel}
                                placeholder={isMr ? "उदा. लोकल सप्लायर" : "e.g. Local Supplier"}
                                value={quickVendor}
                                onChange={(e) => setQuickVendor(e.target.value)}
                                fullWidth
                                size="small"
                            />
                        </>
                    ) : (
                        <FormControl fullWidth size="small">
                            <InputLabel>{txt.reasonLabel}</InputLabel>
                            <Select
                                value={quickReason}
                                label={txt.reasonLabel}
                                onChange={(e) => setQuickReason(e.target.value)}
                            >
                                <MenuItem value="Spoilage / Wastage">{txt.reasonWastage}</MenuItem>
                                <MenuItem value="Staff Food">{txt.reasonStaff}</MenuItem>
                                <MenuItem value="Promo / Free Sample">{txt.reasonSample}</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setQuickModal({ open: false, type: 'inward', item: null })} sx={{ fontWeight: 700, color: '#64748b' }}>
                        {txt.cancel}
                    </Button>
                    <Button 
                        variant="contained" 
                        color={quickModal.type === 'inward' ? 'success' : 'error'}
                        onClick={handleQuickActionSubmit} 
                        disabled={!quickQty || parseFloat(quickQty) <= 0}
                        sx={{ fontWeight: 800 }}
                    >
                        {quickModal.type === 'inward' ? txt.btnSubmitInward : txt.btnSubmitOutward}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG 3: Edit Raw Material */}
            <Dialog open={editModal.open} onClose={() => setEditModal({ open: false, item: null })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>
                    {txt.editModalTitle}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        autoFocus
                        label={txt.itemNameLabel}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        fullWidth
                        size="small"
                        required
                    />
                    <FormControl fullWidth size="small">
                        <InputLabel>{txt.unitLabel}</InputLabel>
                        <Select
                            value={editUnit}
                            label={txt.unitLabel}
                            onChange={(e) => setEditUnit(e.target.value)}
                        >
                            <MenuItem value="नग">{isMr ? "नग (Pcs / Number)" : "Pcs / Number (नग)"}</MenuItem>
                            <MenuItem value="वाटी">{isMr ? "वाटी / पोर्शन (Bowl / Portion)" : "Bowl / Portion (वाटी)"}</MenuItem>
                            <MenuItem value="प्लेट">{isMr ? "प्लेट (Plate)" : "Plate (प्लेट)"}</MenuItem>
                            <MenuItem value="kg">{isMr ? "किलो (kg)" : "Kilogram (kg)"}</MenuItem>
                            <MenuItem value="ग्रॅम">{isMr ? "ग्रॅम (Grams)" : "Grams (gm)"}</MenuItem>
                            <MenuItem value="लिटर">{isMr ? "लिटर (Litre)" : "Litre (ltr)"}</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label={txt.currentStockCol}
                        type="number"
                        value={editQty}
                        onChange={(e) => setEditQty(e.target.value)}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label={txt.alertLevelCol}
                        type="number"
                        value={editThreshold}
                        onChange={(e) => setEditThreshold(e.target.value)}
                        fullWidth
                        size="small"
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setEditModal({ open: false, item: null })} sx={{ fontWeight: 700, color: '#64748b' }}>
                        {txt.cancel}
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleEditItemSubmit} 
                        sx={{ bgcolor: '#008751', fontWeight: 800, '&:hover': { bgcolor: '#006f42' } }}
                    >
                        {txt.update}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
